<script context="module">
  // Auto-locate is a page-load action, not a mount action. On mobile AppShell
  // unmounts the map controls while a playground is selected, so this component
  // remounts every time the panel closes. Without this flag each remount re-ran
  // auto-locate: the map jumped back to the GPS fix and the nearby list
  // reopened, even when the user had been browsing another town (#925).
  // Set before the permission check, so a permission granted later in the
  // session (via this button) does not arm auto-locate on the next remount.
  let autoLocateAttempted = false;
</script>

<script>
  import { fromLonLat } from 'ol/proj';
  import { mapStore } from '../stores/map.js';
  import { location } from '../stores/location.js';
  import { parseHash } from '../lib/deeplink.js';
  import { isRegionPath, shouldAutoCenterOnLocate } from '../lib/regionUrl.js';
  import { regionFramingApplied } from '../stores/urlFraming.js';
  import { get } from 'svelte/store';
  import { Navigation2, Loader2 } from 'lucide-svelte';
  import { _ } from 'svelte-i18n';
  import { onMount, onDestroy } from 'svelte';

  let locating = false;
  let error = '';
  let watchId = null;
  let destroyed = false;

  function locate({ centerMap = true } = {}) {
    if (!navigator.geolocation) {
      error = $_('locate.notSupported');
      return;
    }

    if (watchId) {
      navigator.geolocation.clearWatch(watchId);
      watchId = null;
    }

    locating = true;
    error = '';

    navigator.geolocation.getCurrentPosition(
      pos => {
        locating = false;
        const { latitude, longitude, accuracy } = pos.coords;
        location.set({ lat: latitude, lon: longitude, accuracy });
        if (centerMap) {
          const coord = fromLonLat([longitude, latitude]);
          $mapStore?.getView().animate({ center: coord, zoom: 16 });
        }
        startWatch();
      },
      err => {
        locating = false;
        error = err.code === 1 ? $_('locate.denied') : $_('locate.unavailable');
      },
      { timeout: 10000, maximumAge: 60000, enableHighAccuracy: true }
    );
  }

  // Keep the location store fresh without touching the view.
  function startWatch() {
    // A fix can land after this instance was unmounted (the mobile controls
    // are torn down while a deeplinked playground opens). A watch started then
    // would never be cleared; the next mount resumes it instead.
    if (destroyed) return;
    if (watchId) navigator.geolocation.clearWatch(watchId);
    watchId = navigator.geolocation.watchPosition(
      pos => {
        const { latitude, longitude, accuracy } = pos.coords;
        location.set({ lat: latitude, lon: longitude, accuracy });
      },
      err => {},
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  }

  /**
   * Resolve once region framing settles to a definite value (or after a
   * timeout). The store starts `null` and StandaloneApp/HubApp flips it to
   * `true`/`false` once Nominatim resolution completes — which can land after
   * the GPS fix, so we wait rather than read opportunistically.
   *
   * The timeout is only a safety net for the (unexpected) case where the
   * producer never sets the store. It MUST outlast the producer's worst case:
   * StandaloneApp resolves `fetchRegionInfo` (5 s) then `resolveRegionFromPath`
   * (Nominatim, 3 s) sequentially before it writes the store (~8 s). A shorter
   * timeout would give up early, read `null`, and wrongly suppress centering.
   */
  function waitForFraming(timeoutMs = 9000) {
    if (get(regionFramingApplied) !== null) return Promise.resolve(get(regionFramingApplied));
    return new Promise(resolve => {
      let unsub;
      const timer = setTimeout(() => { unsub?.(); resolve(get(regionFramingApplied)); }, timeoutMs);
      unsub = regionFramingApplied.subscribe(v => {
        if (v !== null) { clearTimeout(timer); unsub?.(); resolve(v); }
      });
    });
  }

  onMount(async () => {
    // Remount after the page-load attempt: only resume the watch that onDestroy
    // cleared, and only if a fix exists, so the location dot keeps updating.
    if (autoLocateAttempted) {
      if (get(location) && navigator.geolocation) startWatch();
      return;
    }
    autoLocateAttempted = true;
    try {
      const perm = await navigator.permissions.query({ name: 'geolocation' });
      if (perm.state !== 'granted') return;
      // Explicit URL framing — a deeplink hash or a *resolved* region path
      // (e.g. /Frankfurt) — expresses where the user wants the map, so don't
      // pan over it with the GPS fix. The location dot still shows. A region
      // path that fails to resolve (typo) does NOT suppress centering, so the
      // user is not silently stranded on the fallback region. For a region
      // path we wait for the framing outcome before deciding.
      const hasDeeplink = !!parseHash(window.location.hash);
      const regionPath = isRegionPath(window.location.pathname);
      const framingApplied = regionPath && !hasDeeplink ? await waitForFraming() : null;
      locate({
        centerMap: shouldAutoCenterOnLocate({ hasDeeplink, regionPath, framingApplied }),
      });
    } catch {
      // permissions API not supported — skip auto-locate
    }
  });

  onDestroy(() => {
    destroyed = true;
    if (watchId) {
      navigator.geolocation.clearWatch(watchId);
    }
  });
</script>

<button
  class="control-btn"
  class:error={!!error}
  onclick={() => locate()}
  disabled={locating}
  title={error || $_('locate.title')}
  aria-label={$_('locate.title')}
>
  {#if locating}
    <Loader2 class="h-5 w-5 animate-spin" />
  {:else}
    <Navigation2 class="h-5 w-5" />
  {/if}
</button>

<style>
  .control-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    background: white;
    border: none;
    border-radius: 50%;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
    cursor: pointer;
    color: #666;
    transition: background 0.15s, color 0.15s;
  }

  .control-btn:hover {
    background: #f5f5f5;
    color: #333;
  }

  .control-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .control-btn.error {
    color: #d93025;
  }
</style>
